import { TransmissionMessage } from '../../../reducers/messagingSlice';

/**
 * AI-suggestion consumption (AI -> HITL, the "advise" side of the co-pilot).
 * The AI emits an `ai_suggestion` event family discriminated by `suggestion_type`;
 * HITL consumes it and renders it as a one-tap suggestion the human agent can
 * accept or dismiss. The AI never acts on its own once the human has intercepted
 * (the driver rule) — these are advisory only.
 */

export enum SuggestionVerdict {
  // Render the suggestion as an actionable card.
  offer = 'offer',
  // Retract: clears any active suggestion of the same suggestion_type.
  skip = 'skip',
}

/**
 * The concrete cart mutation the agent applies on Accept. Brand-menu-shaped
 * (item name + constraint), symmetric with the `agent_cart` event, so it maps
 * straight onto a cartSlice mutation.
 */
export interface ISuggestionAction {
  op: 'add_option' | 'add_item' | 'set_quantity' | 'remove';
  target_path: string[];
  constraint_name?: string;
  option?: { item: string; quantity: number };
  quantity?: number;
}

export interface IAISuggestion {
  suggestion_type: string; // discriminator: send_tts, upsell_eligibility, ...
  suggestion_id: string; // correlation for accept/dismiss + the future QA signal
  verdict: SuggestionVerdict | string;
  source?: string; // "AI"
  title: string; // agent-facing label
  reason?: string; // human-readable why
  reason_code?: string; // machine code (mirrors the AI's reason codes)
  action?: ISuggestionAction; // what Accept applies to the cart
  tts?: string; // when present, the line HITL speaks on Accept (any suggestion_type)
  request_id?: string; // echoes the agent_cart request_id this is a response to
}

export interface IAISuggestionTransmissionMessage extends TransmissionMessage {
  data: IAISuggestion;
}

export interface IAISuggestionsState {
  // The single active suggestion, keyed by its suggestion_type. Each newly
  // received suggestion replaces the map wholesale (it reflects the latest cart,
  // so the prior one is stale), so there is only ever one entry. Accept/Dismiss
  // clear the current card; a re-sent suggestion shows again (the AI controls
  // re-emission — if it re-sends, it's still relevant).
  byType: Record<string, IAISuggestion>;
  // request_id of the most recent agent_cart we emitted; used to drop AI
  // suggestions that answer a superseded cart.
  lastRequestId?: string;
}
